import { fetchClient } from '@/util/fetchClient';

/**
 * The survey requests `fetchSurveysGateway` makes. They live apart from
 * `survey.ts` because its query options default to that gateway, which would
 * otherwise import back into the file that imports it.
 */
export async function getCurrentSurveyByGroup(surveyGroupId: string) {
  const { data } = await fetchClient.GET('/v2/surveys/{groupIdOrSlug}/current', {
    params: { path: { groupIdOrSlug: surveyGroupId } },
  });

  return data?.data;
}

export async function getMySurveyResponses() {
  const { data } = await fetchClient.GET('/v2/surveys/responses/me');

  return data?.data ?? [];
}

/**
 * Public counterpart of `getCurrentSurveyByGroup` for unauthenticated
 * guests: resolves a survey group to whichever version is currently
 * published, with no session required, so a guest reference (e.g. an env
 * var) can hold a stable group id instead of a version-specific definition
 * id that goes stale on republish.
 */
export async function getPublicSurveyByGroup(surveyGroupId: string) {
  const { data } = await fetchClient.GET('/v2/public/surveys/{groupIdOrSlug}/current', {
    params: { path: { groupIdOrSlug: surveyGroupId } },
  });

  return data?.data;
}

/**
 * Submits a guest survey response. The backend stores the guest's name and
 * `email` as the respondent identity in place of a user id and does not
 * de-duplicate submissions by email. `npi` is optional: when present it lets
 * the backend match the respondent to a registry record and validate them as
 * an oncologist.
 */
export async function submitPublicSurveyResponse(
  surveyId: string,
  guest: { fullName: string; email: string; npi?: string },
  answers: Record<string, unknown>
) {
  const { data } = await fetchClient.POST('/v2/public/surveys/{id}/responses', {
    params: { path: { id: surveyId } },
    body: { ...guest, answers },
  });

  return data?.data;
}
